import type { JobCenterState, JobCenterJob } from './jobCenter.types';

const STORAGE_KEY = 'grid.job_center.v1';
const DISMISSED_KEY = 'grid.job_center.dismissed_ids';

const DEFAULT_STATE: JobCenterState = {
  is_open: false,
  user_closed_at: null,
  jobs: {},
  schema_version: 1,
  updated_at: new Date().toISOString(),
};

export const JobCenterStorage = {
  key: STORAGE_KEY,

  load(): JobCenterState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_STATE };
      const parsed = JSON.parse(raw) as Partial<JobCenterState>;
      if (!parsed || parsed.schema_version !== 1 || typeof parsed !== 'object') return { ...DEFAULT_STATE };

      return {
        ...DEFAULT_STATE,
        ...parsed,
        jobs: typeof parsed.jobs === 'object' && parsed.jobs ? (parsed.jobs as Record<string, JobCenterJob>) : {},
      };
    } catch {
      return { ...DEFAULT_STATE };
    }
  },

  save(state: JobCenterState): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...state,
          schema_version: 1,
          updated_at: new Date().toISOString(),
        }),
      );
    } catch {
      // best-effort persistence; never block UI
    }
  },

  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },

  saveDismissed(ids: Set<string>): void {
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(ids)));
    } catch {
      // best-effort; never block UI
    }
  },

  loadDismissed(): Set<string> {
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set();
      return new Set(parsed.filter((v): v is string => typeof v === 'string'));
    } catch {
      return new Set();
    }
  },

  pruneDismissed(ids: Set<string>, activeJobIds: Set<string>): Set<string> {
    const pruned = new Set<string>();
    for (const id of ids) {
      if (activeJobIds.has(id)) {
        pruned.add(id);
      }
    }
    return pruned;
  },

  prune(state: JobCenterState, opts?: { ttlMs?: number; keepTerminalMs?: number }): JobCenterState {
    const ttlMs = opts?.ttlMs ?? 24 * 60 * 60 * 1000; // 24h absolute TTL
    const keepTerminalMs = opts?.keepTerminalMs ?? 5 * 60 * 1000; // keep terminal jobs for 5m

    const now = Date.now();
    const jobs: Record<string, JobCenterJob> = {};

    for (const [jobId, job] of Object.entries(state.jobs || {})) {
      const updatedAtMs = safeParseMs(job.last_updated_at) ?? safeParseMs(job.created_at) ?? now;
      const ageMs = now - updatedAtMs;

      // Drop anything too old
      if (ageMs > ttlMs) continue;

      const isTerminal = job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled';
      if (isTerminal && ageMs > keepTerminalMs) continue;

      jobs[jobId] = job;
    }

    return { ...state, jobs };
  },
};

function safeParseMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}


